import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import TopBar from './components/TopBar';
import BottomTabBar from './components/BottomTabBar';
import DashboardTab from './components/DashboardTab';
import CamerasTab from './components/CamerasTab';
import AlertsTab from './components/AlertsTab';
import SettingsTab from './components/SettingsTab';
import streamService from './services/streamService';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { CameraSessionsProvider } from './context/CameraSessionsContext';

function MainApp() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'dashboard');
  const [streams, setStreams] = useState([]);
  const [streamsLoading, setStreamsLoading] = useState(true);
  const [streamsError, setStreamsError] = useState(null);
  const highlightEventId = searchParams.get('eventId');
  const [recentCount, setRecentCount] = useState(0);
  const [systemStatus, setSystemStatus] = useState('Checking systems…');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const refreshStreams = async () => {
    try {
      const available = await streamService.getAvailableStreams();
      setStreams(Object.values(available));
    } catch (err) {
      console.error('Error refreshing streams:', err);
      setStreamsError('Failed to fetch streams: ' + err.message);
    }
  };

  useEffect(() => {
    const fetchStreams = async () => {
      try {
        const available = await streamService.getAvailableStreams();
        setStreams(Object.values(available));
        setStreamsLoading(false);
      } catch (err) {
        console.error('Error fetching streams:', err);
        setStreamsError('Failed to fetch streams: ' + err.message);
        setStreamsLoading(false);
      }
    };
    fetchStreams();
  }, []);

  const handleToggleDetection = async (streamId, enabled) => {
    try {
      await streamService.updateStream(streamId, { detection_enabled: enabled });
      await refreshStreams();
    } catch (err) {
      console.error('Error toggling detection:', err);
      setStreamsError(err.message);
    }
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'cameras': return <CamerasTab streams={streams} loading={streamsLoading} error={streamsError} onToggleDetection={handleToggleDetection} onRefresh={refreshStreams} />;
      case 'alerts': return <AlertsTab highlightEventId={highlightEventId} />;
      case 'settings': return <SettingsTab />;
      default: return <DashboardTab streams={streams} loading={streamsLoading} error={streamsError} onNavigate={setActiveTab} onRecentCount={setRecentCount} onSystemStatus={setSystemStatus} />;
    }
  };

  return (
    <CameraSessionsProvider streams={streams}>
      <TopBar activeTab={activeTab} onRefresh={refreshStreams} />
      <main className="App-content">
        {renderTab()}
      </main>
      <BottomTabBar activeTab={activeTab} onTabChange={setActiveTab} recentCount={recentCount} systemStatus={activeTab === 'dashboard' ? systemStatus : 'Open dashboard for live status'} />
    </CameraSessionsProvider>
  );
}

function App() {
  const { isAuthenticated } = useAuth();
  return (
    <div className="App">
      <BrowserRouter basename="/app">
        <Routes>
          <Route
            path="/items"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <MainApp />
              </ProtectedRoute>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? '/items' : '/login'} replace />}
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
